import React, { useState } from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import noop from 'lodash/noop';

import themeable from 'zignal-landscape/lib/themeable';

import PageHeader from 'containers/BaseWizard/components/PageHeader';
import PageFooter from 'containers/BaseWizard/components/PageFooter';

const BaseWizardContent = ({
  styles,
  sidebarComponent,
  currentViewIndex = 0,
  children,
  ...rest
}) => {

  const {
    wizardStore = {},
    // actions pass as props
    onClose,
    onCancel,
    // basewizard actions creators
    handlePreviousView,
    handleNextView
  } = rest;

  // base wizard will read from the custom wizard store. Future support
  // for a 'loading' state should read prop from here as well.
  const {
    nextDisabled
  } = wizardStore;

  // ensure a valid child index and find currently viewed child
  let activeIndex = currentViewIndex < 0 ? 0 : currentViewIndex;
  // children isn't an array I guess, need to do this if length is 1
  const reactChildren = React.Children.toArray(children);
  const lastValidIndex = reactChildren.length - 1;
  activeIndex = currentViewIndex > lastValidIndex ? lastValidIndex : activeIndex;
  const currentChild = reactChildren[activeIndex];

  const baseProps = Object.assign({ activeIndex }, rest);
  const childProps = currentChild ? currentChild.props : {};
  // merged props will be passed to custom components e.g. child, sidebar,
  // so they have access to data of the current view. childprops will override any parent props
  const mergedProps = Object.assign({}, baseProps, childProps);

  // onScroll event set on child so that the header knows when to have box shadow
  const [ isScrolled, setScroll ] = useState(false);
  const onScroll = (event) => {
    event.target.scrollTop === 0
      ? setScroll(false)
      : isScrolled === false && setScroll(true);
  };
  const childContent = currentChild && React.cloneElement(currentChild, mergedProps);
  const child = <div onScroll={onScroll} className={styles.content}>{childContent}</div>;

  // these all are optional
  let header;
  let footer;
  let sidebar;

  const {
    pageHeader,
    enableFooter,
  } = mergedProps;

  // create header if enabled through parent or child
  if (pageHeader) {
    // TODO - have option to pass custom header component, pass along mergedProps to component
    header = <PageHeader isScrolled={isScrolled}>{pageHeader}</PageHeader>;
  }

  // clone sidebar and pass parent props
  if (sidebarComponent) {
    // pull titles from the children pages
    const pageTitles = map(reactChildren, 'props.title');
    const sidebarProps = { pageTitles , ...mergedProps, ...sidebarComponent.props };
    const sidebarContent = React.cloneElement(sidebarComponent, sidebarProps);
    // TODO: may need to add support later for different sidebar widths
    sidebar = <div className={styles.Sidebar}>{sidebarContent}</div>;
  }

  // create footer if enabled through parent or child
  if (enableFooter) {
    const isLastChild = activeIndex === lastValidIndex;
    const defaultButtonText = isLastChild ? 'Apply' : 'Next';
    // pass default footer props along with child and parent props
    const footerProps = {
      onFooterCancel: onCancel,
      onFooterBack: activeIndex > 0 ? handlePreviousView : undefined,
      // TODO: handle last view. Generally, 'onFooterNext' will need to be a custom prop handled
      // by each child, as custom validation will usually be required. Children can handle this
      // in a saga and the BaseWizard action to move to the next page can be called from
      // within the custom wizard saga.
      onFooterNext: isLastChild ? onClose : handleNextView,
      buttonText: defaultButtonText,
      nextDisabled,
      ...mergedProps
    };
    // TODO - allow for custom footer component to be passed as prop
    footer = <PageFooter {...footerProps} />;
  }

  return (
    <div className={styles.BaseWizardContent}>
      { sidebar }
      <div className={styles.Page}>
        { header }
        { child }
        { footer }
      </div>
    </div>
  );
};

BaseWizardContent.propTypes = {
  styles: PropTypes.object.isRequired,
  children: PropTypes.node,
  currentViewIndex: PropTypes.number,
  sidebarComponent: PropTypes.node
};

export default themeable(BaseWizardContent);
